<template>
  <div class="nav-picker">
    <swiper :options="swiperOptions" v-if="swiperVisible">
      <swiper-slide
        v-for="img in imagesList"
        :key="img.id"
        @click.native="setSelectedPost(img.post)"
      >
        <div class="nav-picker__slide">
          <v-img
            :src="img.before.src"
            :lazy-src="img.before.lazySrc"
            aspect-ratio="1"
            class="grey lighten-4 nav-picker__slide-image"
          >
            <v-layout
              slot="placeholder"
              fill-height
              align-center
              justify-center
              ma-0
            >
              <v-progress-circular indeterminate color="grey lighten-5" />
            </v-layout>
          </v-img>
          <v-img
            :src="img.after.src"
            :lazy-src="img.after.lazySrc"
            aspect-ratio="1"
            class="grey lighten-5 nav-picker__slide-image"
          >
            <v-layout
              slot="placeholder"
              fill-height
              align-center
              justify-center
              ma-0
            >
              <v-progress-circular indeterminate color="grey lighten-5" />
            </v-layout>
          </v-img>
        </div>
      </swiper-slide>
      <div class="nav-picker__prev" slot="button-prev">
        <v-icon>mdi-chevron-left</v-icon>
      </div>
      <div class="nav-picker__next" slot="button-next">
        <v-icon>mdi-chevron-right</v-icon>
      </div>
    </swiper>
    <div
      class="nav-picker__nothing"
      :class="{ nothing__loading: loading }"
      v-else
    >
      <span
        >Sorry, but nothing matched selected filters.
        <div class="nav-picker__nothing-reset mt-1">Try to reset filters.</div>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex'

export default {
  name: 'NavigationSwiper',
  data() {
    return {
      swiperOptions: {
        watchOverflow: true,
        navigation: {
          nextEl: '.nav-picker__next',
          prevEl: '.nav-picker__prev',
        },
        slidesPerView: 6,
        breakpoints: {
          1264: {
            slidesPerView: 5,
          },
          960: {
            slidesPerView: 4,
          },
          600: {
            slidesPerView: 3,
          },
          400: {
            slidesPerView: 2,
          },
        },
      },
    }
  },
  watch: {
    filteredPosts: {
      handler(newFilteredPosts) {
        const { selectedPost, setSelectedPost } = this

        // If filtered posts list changed we try
        // to pick the first post if current selected post
        // isn't presented in new list
        if (newFilteredPosts.length) {
          if (selectedPost === null) {
            setSelectedPost(newFilteredPosts[0])
          } else {
            const selectedId = selectedPost.id
            const currentSelectedPresented = newFilteredPosts.filter(
              ({ id }) => id === selectedId
            )[0]

            if (!currentSelectedPresented) {
              setSelectedPost(newFilteredPosts[0])
            }
          }
        } else {
          setSelectedPost(null)
        }
      },
    },
  },
  computed: {
    ...mapGetters(['filteredPosts']),
    ...mapState(['loading', 'selectedPost']),
    imagesList() {
      return this.filteredPosts
        .map(post => {
          const { acf } = post
          const {
            after_image: { sizes: after },
            before_image: { sizes: before },
          } = acf.gallery_images[0]

          if (before && after) {
            return {
              id: post.id,
              before: {
                src: before.shop_catalog,
                lazySrc: before.shop_thumbnail,
              },
              after: {
                src: after.shop_catalog,
                lazySrc: after.shop_thumbnail,
              },
              post,
            }
          }

          return null
        })
        .filter(e => e !== null)
    },
    swiperVisible() {
      return this.filteredPosts.length > 0
    },
  },
  methods: {
    ...mapMutations({ setSelectedPost: 'SET_SELECTED_POST' }),
  },
}
</script>

<style lang="scss">
.nav-picker {
  width: 100%;

  .swiper-container {
    background-color: rgba(0, 0, 0, 0.3);
    transition: background-color 0.3s ease-in-out;
  }

  &:hover {
    .nav-picker__slide:before {
      visibility: visible;
      opacity: 1;
    }

    .swiper-container {
      background-color: rgba(0, 0, 0, 0.8);
    }
  }
}

.nav-picker__slide {
  display: flex;
  cursor: pointer;
  user-select: none;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease-in-out, visibility 0.3s linear;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 1;
  }

  &:hover {
    &:before {
      visibility: hidden !important;
      opacity: 0 !important;
    }
  }
}

.nav-picker__slide-image {
  width: 50%;
  flex-grow: 0;
}

.nav-picker__nothing {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: 24px;
  text-align: center;
  height: 106.5px;

  &__loading {
    color: rgba(0, 0, 0, 0);
  }
}

.nav-picker__nothing-reset {
  font-size: 18px;
}

.nav-picker__next,
.nav-picker__prev {
  display: none;
  justify-content: center;
  align-items: center;
  border: 1px solid #eaebeb;
  box-shadow: 0 6px 6px 0 rgba(0, 0, 0, 0.05);
  width: 41px;
  height: 41px;
  border-radius: 51%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  .v-icon {
    font-size: 30px;
    color: white;
  }

  @media (min-width: 960px) {
    display: flex;
  }

  &:hover {
    background-color: white;
    transition: color background-color 0.3s ease;

    .v-icon {
      color: #879ba1;
    }
  }
}

.nav-picker__next {
  right: 40px;
}

.nav-picker__prev {
  left: 40px;
}
</style>
